import { z } from 'zod';
import type { QuestionType } from '@/lib/questionHints';

export type { QuestionType };

const DIGITS_RE = /\d/;
const ZIP_US_RE = /^\d{5}(-\d{4})?$/;
const ZIP_CA_RE = /^[A-Za-z]\d[A-Za-z]\s?\d[A-Za-z]\d$/;

export const nameAnswerSchema = z
  .string()
  .trim()
  .min(1, 'This field is required')
  .max(50, 'Name must be 50 characters or less')
  .refine((v) => !DIGITS_RE.test(v), 'Name should not contain numbers');

export const zipAnswerSchema = z
  .string()
  .trim()
  .min(1, 'This field is required')
  .refine(
    (v) => ZIP_US_RE.test(v) || ZIP_CA_RE.test(v),
    'Please enter a valid zip/postal code',
  );

export const emailAnswerSchema = z
  .string()
  .trim()
  .min(1, 'This field is required')
  .email('Please enter a valid email address');

export const phoneAnswerSchema = z
  .string()
  .transform((v) => v.replace(/\D/g, ''))
  .pipe(
    z.string()
      .min(1, 'This field is required')
      .min(10, 'Please enter a valid phone number'),
  );

export const requiredStringSchema = z
  .string()
  .trim()
  .min(1, 'This field is required');

export const singleChoiceSchema = z.object({
  questionType: z.literal('single_choice'),
  answer: z.string().min(1, 'Please select an option'),
});

export const multipleChoiceSchema = z.object({
  questionType: z.literal('multiple_choice'),
  answer: z.array(z.string()).min(1, 'Please select at least one option'),
});

export const ratingSchema = z.object({
  questionType: z.literal('rating'),
  answer: z.coerce.number().int().min(1).max(5),
});

export const scaleSchema = z.object({
  questionType: z.literal('scale'),
  answer: z.coerce.number().int().min(1).max(10),
});

export const dateSchema = z.object({
  questionType: z.literal('date'),
  answer: z.string().min(1, 'Please select a date'),
});

export const openEndedSchema = z.object({
  questionType: z.literal('open_ended'),
  answer: requiredStringSchema,
});

export const surveyAnswerSchema = z.discriminatedUnion('questionType', [
  singleChoiceSchema,
  multipleChoiceSchema,
  ratingSchema,
  scaleSchema,
  dateSchema,
  openEndedSchema,
]);

export type SurveyAnswer = z.infer<typeof surveyAnswerSchema>;

type InputHintKind = 'name' | 'zip' | 'email' | 'phone' | 'default';

const NAME_RE = /\bname\b/i;
const FIRST_NAME_RE = /\bfirst\s*name\b/i;
const LAST_NAME_RE = /\blast\s*name\b/i;
const ZIP_RE = /\b(zip|postal)\b/i;
const EMAIL_RE = /\bemail\b/i;
const PHONE_RE = /\b(phone|mobile|cell)\b/i;

/**
 * Given a question's text, return the appropriate zod schema for
 * validating open-ended answers. Mirrors the detection logic in
 * questionHints.ts but returns a zod schema instead of an imperative
 * validate function.
 */
export function getAnswerSchema(questionText: string): {
  kind: InputHintKind;
  schema: z.ZodTypeAny;
} {
  if (FIRST_NAME_RE.test(questionText) || LAST_NAME_RE.test(questionText)) {
    return { kind: 'name', schema: nameAnswerSchema };
  }
  if (NAME_RE.test(questionText) && !LAST_NAME_RE.test(questionText) && !FIRST_NAME_RE.test(questionText)) {
    return { kind: 'name', schema: nameAnswerSchema };
  }
  if (ZIP_RE.test(questionText)) {
    return { kind: 'zip', schema: zipAnswerSchema };
  }
  if (EMAIL_RE.test(questionText)) {
    return { kind: 'email', schema: emailAnswerSchema };
  }
  if (PHONE_RE.test(questionText)) {
    return { kind: 'phone', schema: phoneAnswerSchema };
  }
  return { kind: 'default', schema: requiredStringSchema };
}
